<script setup>
  import icon from '@/components/icon'
  import { ref } from 'vue'
  import { use } from '@/use/statements'
  import { use_keymap } from '@/use/key-commands'
  const emit = defineEmits(['toggle-keyboard', 'tab-next'])
  defineOptions({ inheritAttrs: false })
  const { save } = use()
  const thought_text = ref(null)

  const on_prepare_thought = async () => {
    emit('toggle-keyboard', false)
    const textarea = document.querySelector('textarea#wat')
    if (textarea) textarea.style.height = ''
    const text = thought_text.value
    thought_text.value = null
    if (typeof text !== 'string') return

    const trimmed = text.trim()
    if (!trimmed) return

    await save(trimmed)

    if (thought_text.value === text) thought_text.value = null
  }

  const on_adjust_height = event => {
    const textarea = event.target
    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight}px`
  }

  const { register } = use_keymap('Thoughts')
  register('thought::Save', () => on_prepare_thought())
  register('thought::New_Line', () => {
    const textarea = document.querySelector('textarea#wat')
    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const { value } = textarea
      textarea.value = `${value.substring(0, start)}\n${value.substring(end)}`
      textarea.selectionStart = textarea.selectionEnd = start + 1
      thought_text.value = textarea.value
      on_adjust_height({ target: textarea })
    }
  })
  register('thought::Cancel', () => {
    thought_text.value = null
    emit('toggle-keyboard', false)
  })
</script>

<template>
  <fieldset>
    <textarea
      id="wat"
      v-bind="$attrs"
      v-model="thought_text"
      cols="1"
      rows="1"
      placeholder=""
      aria-label="What are you thinking?"
      :spellcheck="true"
      @input="on_adjust_height"
      @focusout="on_prepare_thought"
      @keydown.tab.exact="e => emit('tab-next', e)" />
    <button id="done" type="button" @click="on_prepare_thought">
      <icon name="finished" />
    </button>
  </fieldset>
</template>

<style lang="stylus">
  section#thoughts > fieldset:has(> textarea#wat) {
    display: flex;
    flex-direction: column;
    gap: base-line;
    padding: base-line;
    margin: 0;
    border: none;
    inline-size: 100%;
    min-inline-size: 0;
    &:focus-within {
      inline-size: 100%;
      max-inline-size: page-width;
      margin-inline: auto;
    }
    & > textarea#wat {
      line-height: 1.5;
      padding: (base-line / 2) base-line;
      border-radius: base-line;
      resize: none;
      appearance: none;
      border: round(base-line * 0.1, 2) solid var(--emphasis);
      background-color: var(--basalt-transparent);
      color: var(--rocks);
      caret-color: var(--rocks);
      outline: 0;
      inline-size: 100%;
      min-height: base-line;
      @media (prefers-color-scheme: light) {
        background-color: transparent;
      }
      @media (prefers-color-scheme: dark) {
        color: var(--bone);
        caret-color: var(--bone);
      }
      &::placeholder {
        color: var(--rocks);
        opacity: 0.8;
      }
    }
    & > button#done {
      display: none;
      border: none;
      background: none;
      padding: base-line (base-line * 0.5);
      cursor: pointer;
      align-self: flex-end;
      svg {
        fill: var(--boulders);
      }
    }
  }
  main#realness[data-posting] section#thoughts > fieldset:has(> textarea#wat) {
    & > textarea#wat {
      font-size: 1.25em;
      min-height: base-line * 6;
    }
    & > button#done {
      display: block;
    }
  }
</style>
